import type { StagedAction } from './types'

export const STAGED_ACTIONS_QUERY_KEY = 'staged-actions'

export function stagedActionExpandedQueryKey(id: string): Array<string> {
  return ['staged-action', id, 'expanded']
}

/**
 * List rows are the live, cross-window action source. Expanded reads add
 * conversation metadata, so retain fields absent from the lean row while
 * overlaying every live field it does provide.
 */
export function mergeStagedActionSnapshots(
  expandedAction: StagedAction | null | undefined,
  liveAction: StagedAction | null | undefined,
): StagedAction | null {
  if (!expandedAction) {
    return liveAction ?? null
  }
  if (!liveAction) {
    return expandedAction
  }

  const merged: StagedAction = { ...expandedAction, ...liveAction }
  if (liveAction.conversation !== undefined) {
    return merged
  }

  // Lean list rows — and PATCH responses from a gateway that predates expanded
  // writes — omit conversation metadata. Carry the expanded copy over, but only
  // while it still describes the conversation this action points at, so a
  // regenerated action doesn't inherit the previous conversation's title.
  merged.conversation =
    merged.conversationId === expandedAction.conversationId ? expandedAction.conversation : undefined
  return merged
}

export interface StagedActionListPresence {
  /** The action's row in the live list query, absent once it's gone. */
  liveAction: StagedAction | null | undefined
  /** The live list has answered at least once, so absence from it means something. */
  liveListLoaded: boolean
  /** This action has been in the live list at some point while it's been open. */
  seenInLiveList: boolean
}

/**
 * An action that was in the live list and no longer is has been deleted — here
 * or in another window. Actions that were never in it (a History drill-in) are
 * not deletable this way; the expanded read is their only source.
 *
 * Absence reads as deletion only because the list holds every non-deleted row
 * the user has; a paged action feed would need explicit tombstones instead.
 */
export function isStagedActionDeleted({
  liveAction,
  liveListLoaded,
  seenInLiveList,
}: StagedActionListPresence): boolean {
  return !liveAction && liveListLoaded && seenInLiveList
}

/**
 * The action to show, given both caches: the live row overlaid on the expanded
 * read, or nothing at all once the action has been deleted (which the expanded
 * snapshot it leaves behind would otherwise hide).
 */
export function resolveStagedActionSnapshot(
  expandedAction: StagedAction | null | undefined,
  presence: StagedActionListPresence,
): StagedAction | null {
  if (isStagedActionDeleted(presence)) {
    return null
  }
  return mergeStagedActionSnapshots(expandedAction, presence.liveAction)
}

// Stable empty result so allActions keeps its identity before the first fetch.
export const NO_ACTIONS: Array<StagedAction> = []

const recentlyDeletedIds = new Set<string>()

export function markDeleted(id: string): void {
  recentlyDeletedIds.add(id)
  setTimeout(() => recentlyDeletedIds.delete(id), 5000)
}

export function isRecentlyDeleted(id: string): boolean {
  return recentlyDeletedIds.has(id)
}

export function isMockAction(id: string): boolean {
  return id.startsWith('mock-')
}

export function getSortKey(action: StagedAction): string {
  return `${action.createdAt}-${action.id}`
}

export function normalizeUniqueIds(ids: Array<string>): Array<string> {
  return Array.from(new Set(ids.filter(Boolean)))
}

export function chunkIds(ids: Array<string>, chunkSize: number): Array<Array<string>> {
  const chunks: Array<Array<string>> = []
  for (let i = 0; i < ids.length; i += chunkSize) {
    chunks.push(ids.slice(i, i + chunkSize))
  }
  return chunks
}

/** True when the user has edited this staged action (inline view or NL prompt). */
export function isStagedActionUserEdited(action: Pick<StagedAction, 'metadata'> | null | undefined): boolean {
  return action?.metadata?.userEdited === 'true'
}

/** Pinned under the To-dos Home's Pinned heading: `pinnedAt` in its metadata, blank once unpinned. */
export function isPinnedStagedAction(action: Pick<StagedAction, 'metadata'>): boolean {
  return !!action.metadata?.pinnedAt
}
